import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { UseFormRegister, UseFormSetValue } from "react-hook-form"
import TextField from "@mui/material/TextField"
import { styled, Theme } from "@mui/material/styles"
import InputAdornment from "@mui/material/InputAdornment"
import IconButton from "@mui/material/IconButton"
import SendIcon from "components/atoms/_icons/SendIcon"

type TextFieldProps = {
  id: string;
  defaultValue?: string;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
}

export default function CommentTextField(props: TextFieldProps) {
  const {
    id,
    defaultValue,
    register,
    setValue,
    onSubmit
  } = props

  const [localValue, setLocalValue] = useState("")

  const { t } = useTranslation()

  useEffect(() => {
    setLocalValue(defaultValue || "")
    setValue(id, defaultValue, { shouldDirty: true })
  }, [defaultValue])

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    setLocalValue(event.target.value)
    setValue(id, event.target.value, { shouldDirty: true })
  }

  return (
    <StyledTextField
      id={id}
      value={localValue}
      placeholder={t("items.addComment")}
      type="text"
      multiline
      {...register(id)}
      InputLabelProps={{
        shrink: true
      }}
      InputProps={{
        startAdornment: <InputAdornment
          position="end"
          onClick={onSubmit}
        >
          <IconButton edge="end">
            <SendIconWrapper value={localValue}>
              <SendIcon/>
            </SendIconWrapper>
          </IconButton>
        </InputAdornment>
      }}
      onChange={handleInputChange}
    />
  )
}

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  marginBottom: "4.4rem",
  padding: "0 16px",

  "& button": {
    padding: "0 8px 0 0"
  },

  "& .MuiInputBase-root": {
    marginTop: 0,
    padding: "0 16px 0 0",
    border: `1px solid ${theme?.palette.gray["8"]}`,
    borderRadius: "25px",
    backgroundColor: "#FFFFFF",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",

    "& textarea": {
      padding: "16px 10px",
      fontSize: "1.2rem",
      color: theme?.palette.gray["14"]
    },

    "& .MuiInputAdornment-root": {
      marginLeft: "4px",
      height: "48px",
      maxHeight: "unset"
    },

    "& .MuiInputAdornment-root.not-mandatory": {
      display: "none"
    },

    "& fieldset": {
      display: "none"
    }
  },

  "& .MuiInputBase-root.Mui-focused": {
    border: `1px solid ${theme?.palette.primary["5"]}`,

    "& .MuiInputAdornment-root.not-mandatory": {
      display: "flex"
    }
  }
}))

type SendIconWrapperProps = {
  theme?: Theme;
  value: string;
}

const SendIconWrapper = styled("div")(({ theme, value }: SendIconWrapperProps) => ({
  width: "40px",
  height: "40px",
  backgroundColor: value ? theme?.palette.primary["6"] : theme?.palette.gray["10"],
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "50%",
  transition: "0.3s",

  "& svg": {
    stroke: "#FFFFFF"
  }
}))
